"use client";

import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { MapPin, CircleCheck, ArrowRight, Crosshair, Phone } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const SERVICE_AREAS = [{
  county: "Smith County",
  towns: ["Tyler", "Lindale", "Whitehouse", "Bullard", "Troup"],
  coords: {
    x: 35,
    y: 55
  }
}, {
  county: "Gregg County",
  towns: ["Longview", "Kilgore", "White Oak", "Gladewater"],
  coords: {
    x: 65,
    y: 45
  }
}, {
  county: "Harrison County",
  towns: ["Marshall", "Hallsville", "Waskom", "Harleton"],
  coords: {
    x: 80,
    y: 45
  }
}, {
  county: "Rusk County",
  towns: ["Henderson", "Overton", "Tatum", "Mount Enterprise"],
  coords: {
    x: 65,
    y: 70
  }
}, {
  county: "Wood County",
  towns: ["Mineola", "Quitman", "Winnsboro", "Hawkins"],
  coords: {
    x: 35,
    y: 30
  }
}, {
  county: "Upshur County",
  towns: ["Gilmer", "Big Sandy", "Ore City"],
  coords: {
    x: 60,
    y: 30
  }
}, {
  county: "Cherokee County",
  towns: ["Jacksonville", "Rusk", "Alto"],
  coords: {
    x: 40,
    y: 80
  }
}];
const TacticalMap = () => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;
    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight || 500;
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", width).attr("height", height).attr("viewBox", `0 0 ${width} ${height}`).style("overflow", "hidden");
    const gridSize = 40;
    const gridGroup = svg.append("g").attr("class", "grid-layer").attr("opacity", 0.1);
    for (let x = 0; x <= width; x += gridSize) {
      gridGroup.append("line").attr("x1", x).attr("y1", 0).attr("x2", x).attr("y2", height).attr("stroke", "currentColor").attr("stroke-width", 1);
    }
    for (let y = 0; y <= height; y += gridSize) {
      gridGroup.append("line").attr("x1", 0).attr("y1", y).attr("x2", width).attr("y2", y).attr("stroke", "currentColor").attr("stroke-width", 1);
    }
    const padding = 60;
    const xScale = d3.scaleLinear().domain([0, 100]).range([padding, width - padding]);
    const yScale = d3.scaleLinear().domain([0, 100]).range([padding, height - padding]);
    const points = SERVICE_AREAS.map(area => [xScale(area.coords.x), yScale(area.coords.y)] as [number, number]);
    const centerX = d3.mean(points, d => d[0]) || width / 2;
    const centerY = d3.mean(points, d => d[1]) || height / 2;
    const sortedPoints = points.slice().sort((a, b) => {
      const angleA = Math.atan2(a[1] - centerY, a[0] - centerX);
      const angleB = Math.atan2(b[1] - centerY, b[0] - centerX);
      return angleA - angleB;
    });
    const lineGenerator = d3.line().x(d => d[0]).y(d => d[1]).curve(d3.curveCatmullRomClosed.alpha(0.5));
    const pathData = lineGenerator(sortedPoints);
    const defs = svg.append("defs");
    const gradient = defs.append("radialGradient").attr("id", "zone-gradient").attr("cx", "50%").attr("cy", "50%").attr("r", "50%");
    gradient.append("stop").attr("offset", "0%").attr("stop-color", "var(--primary)").attr("stop-opacity", 0.2);
    gradient.append("stop").attr("offset", "100%").attr("stop-color", "var(--primary)").attr("stop-opacity", 0);
    if (pathData) {
      svg.append("path").attr("d", pathData).attr("fill", "var(--primary)").attr("fill-opacity", 0.1).attr("stroke", "var(--primary)").attr("stroke-width", 2).attr("stroke-dasharray", "5,5").attr("class", "animate-pulse");
    }
    const linksGroup = svg.append("g").attr("class", "links");
    points.forEach(point => {
      linksGroup.append("line").attr("x1", centerX).attr("y1", centerY).attr("x2", point[0]).attr("y2", point[1]).attr("stroke", "var(--primary)").attr("stroke-width", 1).attr("opacity", 0.3);
    });
    const nodesGroup = svg.append("g").attr("class", "nodes");
    SERVICE_AREAS.forEach((area, i) => {
      const [x, y] = points[i];
      const g = nodesGroup.append("g").attr("transform", `translate(${x},${y})`);
      g.append("circle").attr("r", 4).attr("fill", "var(--primary)").append("animate").attr("attributeName", "r").attr("values", "4;12;4").attr("dur", `${2 + Math.random()}s`).attr("repeatCount", "indefinite");
      g.append("circle").attr("r", 12).attr("fill", "var(--primary)").attr("opacity", 0.3).append("animate").attr("attributeName", "opacity").attr("values", "0.3;0;0.3").attr("dur", `${2 + Math.random()}s`).attr("repeatCount", "indefinite");
      g.append("text").attr("y", -15).attr("text-anchor", "middle").attr("fill", "currentColor").attr("font-size", "10px").attr("font-weight", "bold").attr("class", "uppercase tracking-wider font-mono").text(area.towns[0]);
    });
    svg.append("text").attr("x", width - 20).attr("y", height - 20).attr("text-anchor", "end").attr("fill", "var(--muted-foreground)").attr("font-size", "48px").attr("font-weight", "900").attr("opacity", 0.1).attr("class", "uppercase font-serif select-none pointer-events-none").text("EAST TEXAS");
    return () => {
      svg.selectAll("*").remove();
    };
  }, []);
  return <div ref={containerRef} className="w-full h-[400px] lg:h-[600px] relative bg-card/30 border border-border/50 rounded-sm overflow-hidden backdrop-blur-sm">
      <svg ref={svgRef} className="w-full h-full block" />
      
      {}
      <div className="absolute top-4 left-4 flex items-center gap-2 text-xs font-mono text-primary/80">
        <Crosshair className="w-4 h-4 animate-spin-slow" />
        <span>LIVE_COVERAGE_MAP_V2.4</span>
      </div>
      <div className="absolute bottom-4 left-4 text-[10px] font-mono text-muted-foreground">
        LAT: 32.3513° N <br />
        LNG: 95.3011° W
      </div>
    </div>;
};
export default function OurServiceTerritory() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  return <section id="our-service-territory" className="dark bg-background text-foreground py-20 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569013/img/noise_texture.png')] opacity-5 pointer-events-none mix-blend-overlay"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          
          {}
          <div ref={sectionRef} className={`space-y-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-bold uppercase tracking-widest">
                <MapPin className="w-3 h-3" />
                Service Area
              </div>
              <h2 className="text-4xl md:text-5xl font-serif font-bold leading-tight">
                Serving <span className="text-primary">East Texas</span> Landowners
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Based in the heart of East Texas, Shark Bite Land Clearing provides rapid, reliable forestry mulching and land clearing services to ranch owners, developers, and private property holders across the region.
              </p>
            </div>

            {}
            <div className="bg-card/50 border border-border rounded-sm p-1">
              <Accordion type="single" collapsible className="w-full" defaultValue="item-0">
                {SERVICE_AREAS.map((area, index) => <AccordionItem key={index} value={`item-${index}`} className="border-border/50 px-4">
                    <AccordionTrigger className="hover:no-underline hover:text-primary transition-colors py-5">
                      <div className="flex items-center gap-3 text-left">
                        <span className="font-serif text-lg tracking-tight">{area.county}</span>
                        <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider hidden sm:inline-block">
                          
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pb-4">
                        {area.towns.map((town, i) => <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <CircleCheck className="w-3 h-3 text-primary/70 shrink-0" />
                            {town}
                          </div>)}
                      </div>
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>

            <div className="pt-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold tracking-wide rounded-sm h-12 px-8 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.5)] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-all">
                  <Link to="/GetAQuotePage">
                    Get a Free Estimate <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <div className="flex items-center gap-3 px-4 py-2 border-l-2 border-primary/30">
                  <div className="text-sm">
                    <p className="text-muted-foreground font-medium flex items-center gap-2">
                      <Phone className="w-3 h-3" /> Call for service
                    </p>
                    <Link to="tel:9034904941" className="text-foreground font-bold hover:text-primary transition-colors block">
                      (903) 490-4941
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="relative">
              {}
              <div className="absolute -top-2 -left-2 w-8 h-8 border-t-2 border-l-2 border-primary z-20"></div>
              <div className="absolute -bottom-2 -right-2 w-8 h-8 border-b-2 border-r-2 border-primary z-20"></div>
              
              <TacticalMap />
              
              {}
              <div className="absolute bottom-6 right-6 bg-card/90 backdrop-blur-md border border-border p-4 rounded-sm shadow-xl max-w-[200px] hidden sm:block">
                <div className="text-xs font-mono text-muted-foreground mb-1 uppercase">Active Units</div>
                <div className="text-2xl font-bold text-foreground flex items-baseline gap-1">
                  3 <span className="text-sm font-normal text-muted-foreground">Crews</span>
                </div>
                <div className="w-full h-1 bg-muted mt-2 rounded-full overflow-hidden">
                  <div className="h-full bg-primary w-3/4 animate-pulse"></div>
                </div>
                <div className="mt-2 text-[10px] text-muted-foreground">
                  Deploying from East Texas HQ
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}